import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Loader, Pagination, Table } from "semantic-ui-react";
import { getComplianceAPIDateFormat } from "@helpers/dates";
import { getStatusStyle } from "@helpers/prepareDetails";
import { STATUS_MAP } from "@helpers/constant";

const ROWS_PER_PAGE = 25;
const COLUMN_COUNT = 33;

const AssetAdminTableView = ({
  data = [],
  loading = false,
  selectedAssetId = null,
  onSelectRow,
  hideFloatFields = false,
  login,
}) => {

  const { billValidatorDisplay } = login;

  const [activePage, setActivePage] = useState(1);
  const tableScrollRef = useRef(null);
  const [stickyLefts, setStickyLefts] = useState([0, 0, 0]);

  const measureStickyLefts = useCallback(() => {
    const root = tableScrollRef.current;
    if (!root) return;
    const cells = root.querySelectorAll("table thead tr th");
    if (cells.length < 3) return;
    const w0 = cells[0].getBoundingClientRect().width;
    const w1 = cells[1].getBoundingClientRect().width;
    setStickyLefts([0, w0, w0 + w1]);
  }, []);

  useLayoutEffect(() => {
    measureStickyLefts();
    const root = tableScrollRef.current;
    if (!root) return;
    const ro = new ResizeObserver(() => measureStickyLefts());
    ro.observe(root);
    window.addEventListener("resize", measureStickyLefts);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureStickyLefts);
    };
  }, [measureStickyLefts, data.length, activePage, loading]);

  const getStickyColumnStyle = (columnIndex, isHeader = false) => ({
    position: "sticky",
    left: stickyLefts[columnIndex],
    background: "#fff",
    zIndex: isHeader ? 4 : 2,
  });

  const totalPages = Math.max(1, Math.ceil(data.length / ROWS_PER_PAGE));
  const start = (activePage - 1) * ROWS_PER_PAGE;
  const paginatedData = data.slice(start, start + ROWS_PER_PAGE);

  useEffect(() => {
    setActivePage(1);
  }, [data.length]);

  const handlePaginationChange = (event, { activePage: nextPage }) => {
    setActivePage(nextPage);
  };

  if (loading && data.length === 0) {
    return <Loader active content="Loading assets..." />;
  }

  const getGBGAsset = (row) => {
    if (row.variantName.includes("GBG-TITO") || row.variantName.includes("Kiosk ")) return row.gbgAsset;
    else return "";
  };

  const gmuSerial = (row) => {
    const serial = String(row?.gmuSerial ?? "").trim();
    return /^\d{4}-\d{4}-\d{4}$/.test(serial) ? serial : "";
  };

  return (
    <div className="height-100 gs-flex-container-vertical">
      <div className="table-overflow flex-stretch-vertical" ref={tableScrollRef}>
        <Table
          basic="very"
          celled
          sortable
          unstackable
          compact
          selectable
          singleLine
        >
          <Table.Header style={{ position: "sticky", top: 0, zIndex: 3, background: "#fff" }}>
            <Table.Row>
              <Table.HeaderCell style={getStickyColumnStyle(0, true)}>MIN</Table.HeaderCell>
              <Table.HeaderCell style={getStickyColumnStyle(1, true)}>Serial No.</Table.HeaderCell>
              <Table.HeaderCell style={getStickyColumnStyle(2, true)}>Cabinet Type</Table.HeaderCell>
              <Table.HeaderCell>Cabinet Title/Game Pack</Table.HeaderCell>
              <Table.HeaderCell>Manufacturer</Table.HeaderCell>
              <Table.HeaderCell>Variant</Table.HeaderCell>
              <Table.HeaderCell>Category</Table.HeaderCell>
              <Table.HeaderCell>{ billValidatorDisplay ? 'Bill Validator' : 'Note Acceptor' }</Table.HeaderCell>
              <Table.HeaderCell>DP Type</Table.HeaderCell>
              <Table.HeaderCell>GMU Serial</Table.HeaderCell>
              <Table.HeaderCell>GBG Asset</Table.HeaderCell>
              <Table.HeaderCell>MAC Address</Table.HeaderCell>
              <Table.HeaderCell>Min Stake</Table.HeaderCell>
              <Table.HeaderCell>Depot</Table.HeaderCell>
              <Table.HeaderCell>Supplier</Table.HeaderCell>
              <Table.HeaderCell>Ownership </Table.HeaderCell>
              <Table.HeaderCell>Install Status</Table.HeaderCell>
              <Table.HeaderCell>Purchase Price</Table.HeaderCell>
              <Table.HeaderCell>Purchase Date</Table.HeaderCell>
              <Table.HeaderCell>Term</Table.HeaderCell>
              <Table.HeaderCell>Week Fee</Table.HeaderCell>
              <Table.HeaderCell>Week Balance</Table.HeaderCell>
              <Table.HeaderCell>Week Install</Table.HeaderCell>
              <Table.HeaderCell>Week Remaining</Table.HeaderCell>
              {!hideFloatFields && <Table.HeaderCell>Total Float</Table.HeaderCell>}
              {!hideFloatFields && <Table.HeaderCell>Float20</Table.HeaderCell>}
              {!hideFloatFields && <Table.HeaderCell>Float10</Table.HeaderCell>}
              {!hideFloatFields && <Table.HeaderCell>Float1</Table.HeaderCell>}
              {!hideFloatFields && <Table.HeaderCell>Float01</Table.HeaderCell>}
              <Table.HeaderCell>Site</Table.HeaderCell>
              <Table.HeaderCell>Town</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.length === 0 && !loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMN_COUNT} textAlign="center">
                  No assets match the current filters.
                </Table.Cell>
              </Table.Row>
            ) : (
              paginatedData.map((row) => {
                const selected = selectedAssetId === row.assetId;
                const statusInfo =
                  STATUS_MAP[String(row.installStatus)];
                const statusLabel = statusInfo?.label ?? String(row.installStatus ?? "");
                return (
                  <Table.Row
                    key={row.assetId}
                    active={selected}
                    onClick={() => onSelectRow && onSelectRow(row)}
                    style={{ cursor: onSelectRow ? "pointer" : undefined }}
                  >
                    <Table.Cell style={getStickyColumnStyle(0)}>{row.minNumber ?? ""}</Table.Cell>
                    <Table.Cell style={getStickyColumnStyle(1)}>{row.serialNumber ?? ""}</Table.Cell>
                    <Table.Cell style={getStickyColumnStyle(2)}>{row.cabinetName ?? ""}</Table.Cell>
                    <Table.Cell>{row.gameName ?? ""}</Table.Cell>
                    <Table.Cell>{row.manufacturerName ?? ""}</Table.Cell>
                    <Table.Cell>{row.variantName ?? ""}</Table.Cell>
                    <Table.Cell>{row.categoryName ?? ""}</Table.Cell>
                    <Table.Cell>{row.noteAcceptor ?? ""}</Table.Cell>
                    <Table.Cell>{row.dpType ?? ""}</Table.Cell>
                    <Table.Cell>{gmuSerial(row)}</Table.Cell>
                    <Table.Cell>{getGBGAsset(row)}</Table.Cell>
                    <Table.Cell>{row.macAddress ?? ""}</Table.Cell>
                    <Table.Cell>{row.minStake ?? ""}</Table.Cell>
                    <Table.Cell>{row.depotName ?? ""}</Table.Cell>
                    <Table.Cell>{row.supplierName ?? ""}</Table.Cell>
                    <Table.Cell>{row.ownershipName ?? ""}</Table.Cell>
                    <Table.Cell>
                      <div style={getStatusStyle(row.installStatus)}>{statusLabel}</div>
                    </Table.Cell>
                    <Table.Cell>{row.purchasePrice ?? ""}</Table.Cell>
                    <Table.Cell textAlign="center">{(row.purchaseDate) ? getComplianceAPIDateFormat(row.purchaseDate) : "-"}</Table.Cell>
                    <Table.Cell>{row.term ?? ""}</Table.Cell>
                    <Table.Cell>{row.weekContentFee ?? ""}</Table.Cell>
                    <Table.Cell>{row.weekContentBalance ?? ""}</Table.Cell>
                    <Table.Cell>{row.weekSinceInstall ?? ""}</Table.Cell>
                    <Table.Cell>{row.weeksRemaining ?? ""}</Table.Cell>
                    {!hideFloatFields && <Table.Cell>{row.totalFloat ?? ""}</Table.Cell>}
                    {!hideFloatFields && <Table.Cell>{row.float20 ?? ""}</Table.Cell>}
                    {!hideFloatFields && <Table.Cell>{row.float10 ?? ""}</Table.Cell>}
                    {!hideFloatFields && <Table.Cell>{row.float1 ?? ""}</Table.Cell>}
                    {!hideFloatFields && <Table.Cell>{row.float01 ?? ""}</Table.Cell>}
                    <Table.Cell>{row.installSiteName ?? ""}</Table.Cell>
                    <Table.Cell>{row.installSiteTown ?? ""}</Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </div>
      {data.length > 0 && totalPages > 1 && (
        <div className="mt-1 mb-1 flex-end">
          <Pagination
            activePage={activePage}
            totalPages={totalPages}
            onPageChange={handlePaginationChange}
            size="mini"
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    login: state.logIn,
  }
}

export default connect(mapStateToProps, {})(AssetAdminTableView);
